import { useCallback } from 'react';

import {
    useLocationController_CreateLocation,
    useLocationController_DeleteLocation,
    useLocationController_MarkHistorical,
    useLocationController_UpdateLocation,
} from '@/services/hooks';

import {
    type DomainMutation,
    type DomainMutationCallbacks,
    ignoreBody,
    toMutationOptions,
    useMappedMutation,
} from '../shared/mutation';

import { toNewLocationBody, toUpdateLocationBody } from './mappers';
import type { LocationFormInput, LocationUpdateInput, MarkHistoricalInput } from './types';

function markHistoricalToApi({ cageId, historical }: MarkHistoricalInput) {
    return { cageId, queryParams: { historical } };
}

function updateLocationToApi({ id, ...input }: LocationUpdateInput) {
    return { locationId: id, requestBody: toUpdateLocationBody(input) };
}

function locationIdToApi(locationId: number) {
    return { locationId };
}

/** Moves a pen to or from the historical list. */
export function useMarkCageHistorical(
    callbacks?: DomainMutationCallbacks
): DomainMutation<MarkHistoricalInput, void> {
    return useMappedMutation(
        useLocationController_MarkHistorical(toMutationOptions(callbacks, ignoreBody)),
        markHistoricalToApi,
        ignoreBody
    );
}

/** Creates a location for the client from the location form's values. */
export function useCreateLocation(
    clientId: number,
    callbacks?: DomainMutationCallbacks
): DomainMutation<LocationFormInput, void> {
    const toApi = useCallback(
        (input: LocationFormInput) => ({ requestBody: toNewLocationBody(input, clientId) }),
        [clientId]
    );
    return useMappedMutation(
        useLocationController_CreateLocation(toMutationOptions(callbacks, ignoreBody)),
        toApi,
        ignoreBody
    );
}

export function useUpdateLocation(
    callbacks?: DomainMutationCallbacks
): DomainMutation<LocationUpdateInput, void> {
    return useMappedMutation(
        useLocationController_UpdateLocation(toMutationOptions(callbacks, ignoreBody)),
        updateLocationToApi,
        ignoreBody
    );
}

export function useDeleteLocation(): DomainMutation<number, void> {
    return useMappedMutation(useLocationController_DeleteLocation(), locationIdToApi, ignoreBody);
}
